import { createElement, useState } from "react";
import { router } from "@inertiajs/react";
import {
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";

interface AdminUser {
  id: number;
  name: string;
  email: string;
  created_at: string;
  has_logged_in: boolean;
  pack_credits: number;
  total_credits: number;
}

interface AdminsIndexProps {
  admins: AdminUser[];
  flash?: { notice?: string; alert?: string };
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function creditsToDollars(credits: number): string {
  return `$${(credits / 100).toFixed(2)}`;
}

function AdminsIndex({ admins }: AdminsIndexProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [credits, setCredits] = useState("500");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    router.post(
      "/admin/admins",
      {
        admin_invite: { name, email, credits: parseInt(credits, 10) },
      },
      {
        onFinish: () => {
          setSubmitting(false);
          setName("");
          setEmail("");
          setCredits("500");
        },
      }
    );
  };

  const handleRevoke = (userId: number, userEmail: string) => {
    if (confirm(`Revoke admin access for ${userEmail}?`)) {
      router.post(`/admin/admins/${userId}/revoke`);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Admin Users</h1>
        <p className="text-muted-foreground mt-1">
          Invite and manage system administrators
        </p>
      </header>

      {/* Invite Form */}
      <div className="bg-card rounded-lg border border-border shadow-sm p-6 mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Invite Admin
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Creates the user, subscribes them to the Friends & Family plan, grants
          admin access, and sends an invitation email.
        </p>
        <form onSubmit={handleSubmit} className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              placeholder="Jane Doe"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              placeholder="jane@example.com"
            />
          </div>
          <div className="w-40">
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              Credits ({creditsToDollars(parseInt(credits, 10) || 0)} AI spend)
            </label>
            <input
              type="number"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              min="0"
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {submitting ? "Sending..." : "Invite Admin"}
          </button>
        </form>
      </div>

      {/* Admins Table */}
      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                Name
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                Email
              </th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                Credits
              </th>
              <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">
                Logged In
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                Created
              </th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {admins.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No admin users yet. Send your first invite above.
                </td>
              </tr>
            )}
            {admins.map((admin) => (
              <tr
                key={admin.id}
                className="hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-3 font-medium text-foreground">
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheckIcon className="w-4 h-4 text-primary" />
                    {admin.name}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {admin.email}
                </td>
                <td className="px-4 py-3 text-sm text-right text-foreground">
                  {admin.total_credits.toLocaleString()}
                  <span className="text-muted-foreground ml-1">
                    ({creditsToDollars(admin.total_credits)})
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {admin.has_logged_in ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-muted-foreground/50 mx-auto" />
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {formatDate(admin.created_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleRevoke(admin.id, admin.email)}
                    className="inline-flex items-center gap-1 text-sm text-red-500 hover:text-red-400 transition-colors"
                  >
                    <XCircleIcon className="w-4 h-4" />
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

AdminsIndex.layout = (page: React.ReactNode) =>
  createElement(AdminLayout, null, page);

export default AdminsIndex;
